import { useEffect, useMemo, useRef, useState } from "react";
import { useLingui } from "@lingui/react";
import { CloudDownload, Info, ChevronDown, ChevronUp, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AppDialog } from "@/components/common/AppDialog";
import type { ExportRecipesFormData } from "./schemas";
import type { MenuItemRow } from "@/components/recipes/tables";

interface ExportRecipesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (data: ExportRecipesFormData) => void;
  menuItemsCount: number;
  prepRecipesCount: number;
  ingredientsCount: number;
  isPending?: boolean;
  selectedMenuItems?: MenuItemRow[];
  selectedPrepRecipeIds?: number[];
  onRemoveMenuItem?: (id: string | number) => void;
}

function statusVariant(
  status: MenuItemRow["status"],
): "default" | "secondary" | "outline" {
  if (status === "Published") return "default";
  if (status === "Archived") return "secondary";
  return "outline";
}

export function ExportRecipesDialog({
  open,
  onOpenChange,
  onExport,
  menuItemsCount,
  prepRecipesCount,
  ingredientsCount,
  isPending = false,
  selectedMenuItems,
  selectedPrepRecipeIds,
  onRemoveMenuItem,
}: ExportRecipesDialogProps) {
  const { i18n } = useLingui();

  // Local copy so user can remove items from the list without affecting table selection
  const [localMenuItems, setLocalMenuItems] = useState<MenuItemRow[]>(
    selectedMenuItems ?? [],
  );
  const [expanded, setExpanded] = useState(false);
  const [includeMenuItems, setIncludeMenuItems] = useState(true);
  const [includePrepRecipes, setIncludePrepRecipes] = useState(true);
  const [includeIngredients, setIncludeIngredients] = useState(true);
  const [fileType, setFileType] = useState<"xlsx" | "csv">("xlsx");

  const wasOpenRef = useRef(open);

  useEffect(() => {
    const openedNow = open && !wasOpenRef.current;
    if (openedNow) {
      setLocalMenuItems(selectedMenuItems ?? []);
      setExpanded(false);
    }
    wasOpenRef.current = open;
  }, [open, selectedMenuItems]);

  const handleRemove = (id: string | number) => {
    setLocalMenuItems((prev) => prev.filter((item) => item.id !== id));
    onRemoveMenuItem?.(id);
  };

  const localMenuItemIds = localMenuItems.map((item) => Number(item.id));
  const hasMenuItems = localMenuItems.length > 0;
  const hasPrepRecipes =
    selectedPrepRecipeIds && selectedPrepRecipeIds.length > 0;

  const handleExport = () => {
    onExport({
      includeMenuItems,
      includePrepRecipes,
      includeIngredients,
      fileType,
      includeImages: true,
      includeIngredientBreakdown: false,
      selectedMenuItemIds: hasMenuItems ? localMenuItemIds : undefined,
      selectedPrepRecipeIds: hasPrepRecipes ? selectedPrepRecipeIds : undefined,
    });
  };

  const canExport = !isPending && (includeMenuItems || includePrepRecipes);

  const menuItemCount = hasMenuItems ? localMenuItems.length : menuItemsCount;
  const prepRecipeCount = hasPrepRecipes
    ? selectedPrepRecipeIds.length
    : prepRecipesCount;

  // Estimate file size based on the rows that will actually be exported.
  // Rough heuristics: menu items ~8 KB/row (many fields + image URL),
  // prep recipes ~5 KB/row, ingredients ~2 KB/row, plus ~10 KB overhead.
  const estimatedSizeKB = useMemo(() => {
    const mi = includeMenuItems ? menuItemCount : 0;
    const pr = includePrepRecipes ? prepRecipeCount : 0;
    const ing = includeIngredients ? ingredientsCount : 0;
    return 10 + mi * 8 + pr * 5 + ing * 2;
  }, [
    includeMenuItems, menuItemCount,
    includePrepRecipes, prepRecipeCount,
    includeIngredients, ingredientsCount,
  ]);

  const estimatedSizeLabel = useMemo(() => {
    if (estimatedSizeKB >= 1024) {
      return `~${(estimatedSizeKB / 1024).toFixed(1)} MB`;
    }
    return `~${estimatedSizeKB} KB`;
  }, [estimatedSizeKB]);

  const categories = [
    {
      key: "includeMenuItems" as const,
      label: i18n._("Menu items"),
      description: i18n._("Published dishes and items shown on menus."),
      count: menuItemCount,
      checked: includeMenuItems,
      onChange: setIncludeMenuItems,
    },
    {
      key: "includePrepRecipes" as const,
      label: i18n._("Prep recipes"),
      description: i18n._(
        "Sauces, mixes, and prep components used by menu items.",
      ),
      count: prepRecipeCount,
      checked: includePrepRecipes,
      onChange: setIncludePrepRecipes,
    },
    {
      key: "includeIngredients" as const,
      label: i18n._("Ingredients"),
      description: i18n._("Ingredient library with units, allergens, and costs."),
      count: ingredientsCount,
      checked: includeIngredients,
      onChange: setIncludeIngredients,
    },
  ];

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={i18n._("Export recipes")}
      description={i18n._("Choose what to include in your export file.")}
      className="max-w-175"
      hideClose={isPending}
      footer={
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-3 text-sm text-(--color-text-secondary)">
            <Info className="size-5 shrink-0" />
            {isPending ? (
              <span className="font-medium text-(--color-text-primary)">
                {i18n._("Preparing your file…")}
              </span>
            ) : (
              <span>
                {i18n._("Estimated size:")}{" "}
                <span className="font-medium text-(--color-text-primary)">{estimatedSizeLabel}</span>
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
              className="h-13 min-w-43.75 rounded-[0.625rem]"
            >
              {i18n._("Cancel")}
            </Button>
            <Button
              onClick={handleExport}
              disabled={!canExport}
              className="h-13 min-w-43.75 gap-2 rounded-[0.625rem] bg-(--color-text-primary) text-(--color-surface-page) hover:bg-(--color-text-primary)/90"
            >
              {isPending ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <CloudDownload className="size-5" />
              )}
              {isPending ? i18n._("Exporting…") : i18n._("Export")}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Collapsible selected items section */}
        {hasMenuItems && (
          <div className="rounded-xl border">
            <Button
              variant="ghost"
              onClick={() => setExpanded((v) => !v)}
              className="h-auto w-full justify-between px-4 py-3 hover:bg-transparent"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-md border text-sm font-semibold">
                  {localMenuItems.length}
                </span>
                {i18n._("menu items selected.")}
              </span>
              {expanded ? (
                <ChevronUp className="size-4 text-(--color-text-secondary)" />
              ) : (
                <ChevronDown className="size-4 text-(--color-text-secondary)" />
              )}
            </Button>

            {expanded && (
              <div className="border-t">
                {localMenuItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
                  >
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="size-11 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <div className="size-11 shrink-0 rounded-md bg-(--color-surface-muted)" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium">
                          {item.name}
                        </p>
                        <Badge
                          variant={statusVariant(item.status)}
                          className="shrink-0 text-xs"
                        >
                          {item.status}
                        </Badge>
                      </div>
                      {item.recipeCode && (
                        <span className="text-xs text-(--color-text-secondary)">
                          {item.recipeCode}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(item.id)}
                      className="size-9 shrink-0 text-(--color-danger-text) hover:bg-(--color-danger)/10 hover:text-(--color-danger-text)"
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Include section */}
        <div className="space-y-2">
          <Label className="text-base font-medium">
            {i18n._("Include")}
          </Label>
          <div className="space-y-2">
            {categories.map((cat) => (
              <label
                key={cat.key}
                className="flex cursor-pointer items-center justify-between rounded-xl bg-(--color-surface-muted)/60 p-3 transition-colors hover:bg-(--color-surface-muted)"
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={cat.checked}
                    onCheckedChange={(v) => cat.onChange(!!v)}
                  />
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium leading-none text-(--color-text-primary)">
                      {cat.label}
                    </span>
                    <span className="text-sm leading-none text-(--color-text-secondary)">
                      {cat.description}
                    </span>
                  </div>
                </div>
                <div className="ml-3 shrink-0 rounded-full bg-(--color-surface-muted) px-3 py-2">
                  <span className="text-sm font-medium text-(--color-text-secondary)">
                    {cat.count > 0 ? cat.count.toLocaleString() : "—"}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* File type */}
        <div className="space-y-2">
          <Label className="text-base font-medium">
            {i18n._("File type")}
          </Label>
          <Select
            value={fileType}
            onValueChange={(v) => setFileType(v as "xlsx" | "csv")}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="xlsx">{i18n._("Excel (.xlsx)")}</SelectItem>
              <SelectItem value="csv">{i18n._("CSV (.csv)")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </AppDialog>
  );
}
